import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { mediaUrl } from "../../lib/api";
import { fmtUSD, fmtDate } from "./format";

function ProfileHeader({ client }) {
  return (
    <div data-testid="drawer-profile" className="flex items-start gap-4 p-4 border border-[#C9A227]/20 bg-white/[0.02]">
      <div className="w-16 h-16 rounded-full overflow-hidden border border-[#C9A227]/40 flex items-center justify-center bg-white/[0.04] shrink-0">
        {mediaUrl(client.avatar_url) ? (
          <img src={mediaUrl(client.avatar_url)} alt="" data-testid="drawer-avatar" className="w-full h-full object-cover" />
        ) : (
          <span className="font-display text-2xl" style={{ color: client.avatar_color || "#C9A227" }}>
            {(client.name || client.email).slice(0, 1).toUpperCase()}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <div className="text-white font-semibold text-lg leading-tight" data-testid="drawer-name">{client.name || "—"}</div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2 text-sm">
          <div><span className="text-white/40">Handicap: </span><span data-testid="drawer-handicap">{client.handicap || "—"}</span></div>
          <div><span className="text-white/40">Uploads/mo: </span><span data-testid="drawer-monthly">{client.uploads_this_month ?? 0}/{client.monthly_upload_limit ?? 3}</span></div>
        </div>
        <div className="mt-1 text-sm"><span className="text-white/40">Country club: </span><span data-testid="drawer-home-course">{client.home_course || "—"}</span></div>
      </div>
    </div>
  );
}

function ListSection({ testid, label, items, empty, render }) {
  return (
    <div data-testid={testid}>
      <div className="overline" style={{ color: "#A0B0A8" }}>{label} ({items.length})</div>
      <div className="mt-3 space-y-2">
        {items.length === 0 ? <p className="text-white/40 text-sm">{empty}</p> : items.map(render)}
      </div>
    </div>
  );
}

export function ClientDrawer({ open, onOpenChange, detail }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="admin-client-drawer" className="bg-[#08100D] border-l border-[#C9A227]/30 text-white w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display text-3xl text-white">
            {detail ? (detail.client.name || detail.client.email) : "Loading..."}
          </SheetTitle>
          <SheetDescription className="sr-only">Customer record with swings, lessons and payments.</SheetDescription>
        </SheetHeader>
        {!detail ? (
          <div className="mt-8 text-white/40 text-sm">Loading customer record...</div>
        ) : (
          <div className="mt-6 space-y-8">
            <ProfileHeader client={detail.client} />

            {detail.client.bio ? (
              <div data-testid="drawer-bio">
                <div className="overline" style={{ color: "#A0B0A8" }}>Bio</div>
                <p className="text-sm text-white/80 mt-2 whitespace-pre-line">{detail.client.bio}</p>
              </div>
            ) : (
              <div data-testid="drawer-bio" className="text-white/40 text-sm">No bio added.</div>
            )}

            <div className="grid grid-cols-2 gap-3" data-testid="drawer-summary">
              <div className="p-3 border border-white/10">
                <div className="overline" style={{ color: "#A0B0A8" }}>Email</div>
                <div className="text-sm mt-1 break-all">{detail.client.email}</div>
              </div>
              <div className="p-3 border border-white/10">
                <div className="overline" style={{ color: "#A0B0A8" }}>Status</div>
                <div className="text-sm mt-1" style={{ color: detail.client.is_pro ? "#C9A227" : undefined }}>
                  {detail.client.is_pro ? "Pro Member" : "Free"}
                </div>
              </div>
              <div className="p-3 border border-white/10">
                <div className="overline" style={{ color: "#A0B0A8" }}>Total Spent</div>
                <div className="text-sm mt-1 text-[#C9A227]">{fmtUSD(detail.client.total_spent)}</div>
              </div>
              <div className="p-3 border border-white/10">
                <div className="overline" style={{ color: "#A0B0A8" }}>Joined</div>
                <div className="text-sm mt-1">{fmtDate(detail.client.created_at)}</div>
              </div>
            </div>

            <ListSection testid="drawer-swings" label="Swings" items={detail.videos} empty="No swings uploaded."
              render={(v) => (
                <div key={v.video_id} className="flex justify-between gap-3 border-b border-white/5 pb-2 text-sm">
                  <span className="truncate">{v.title}</span>
                  <span className="text-white/40 whitespace-nowrap">{fmtDate(v.created_at)}</span>
                </div>
              )} />

            <ListSection testid="drawer-lessons" label="Lessons" items={detail.lessons} empty="No lessons booked."
              render={(l) => (
                <div key={l.booking_id} className="flex justify-between gap-3 border-b border-white/5 pb-2 text-sm">
                  <span className="truncate">{l.preferred_date} · {l.preferred_time}</span>
                  <span className={`whitespace-nowrap uppercase text-xs ${l.status === "confirmed" ? "text-[#C9A227]" : "text-white/40"}`}>{l.status}</span>
                </div>
              )} />

            <ListSection testid="drawer-payments" label="Payments" items={detail.payments} empty="No payments."
              render={(p) => (
                <div key={p.session_id} className="flex justify-between gap-3 border-b border-white/5 pb-2 text-sm">
                  <span className="truncate">{(p.metadata?.product || "payment").replace(/_/g, " ")}</span>
                  <span className="text-white/40 whitespace-nowrap">{fmtUSD(p.amount)} · {p.payment_status}</span>
                </div>
              )} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
